import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/card';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { createPortal } from 'react-dom';
import { useAuthStore } from '../../store/authStore';
import { aiService } from '../../lib/aiService';

export default function ChatBot() {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, role: 'bot', text: 'Hi! I am your AI assistant. How can I help you today?' }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef(null);

  const questionsByRole = {
    admin: [
      "List pending appraisals",
      "System health status",
      "Recent user activity",
      "Generate monthly report"
    ],
    supervisor: [
      "Team appraisal status",
      "Pending approvals",
      "Team goals summary",
      "Schedule team review"
    ],
    employee: [
      "My appraisal status",
      "Update my goals",
      "Feedback received",
      "Next review date"
    ]
  };

  const currentRole = user?.role || 'employee';
  const suggestedQuestions = questionsByRole[currentRole] || questionsByRole.employee;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleSend = async (text = null) => {
    const messageText = typeof text === 'string' ? text : inputValue;
    if (!messageText.trim()) return;

    const userMessage = { id: Date.now(), role: 'user', text: messageText };
    const newHistory = [...messages, userMessage];
    
    setMessages(newHistory);
    setInputValue('');
    setIsTyping(true);

    try {
      const responseText = await aiService.chatWithBot(newHistory, currentRole);
      
      setMessages(prev => [
        ...prev, 
        { id: Date.now() + 1, role: 'bot', text: responseText }
      ]);
    } catch (error) {
      console.error(error);
      setMessages(prev => [
        ...prev, 
        { id: Date.now() + 1, role: 'bot', text: error.message || "I'm sorry, I'm having trouble connecting right now. Please try again later." }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSend();
  };

  // Render directly into body/portal to ensure it floats above everything
  return createPortal(
    <div className="fixed bottom-6 right-6 z-[100] flex flex-col items-end gap-4 font-sans">
      {isOpen && (
        <Card className="w-[350px] h-[500px] shadow-2xl border-0 flex flex-col animate-in slide-in-from-bottom-5 fade-in duration-200">
          <CardHeader className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-t-xl p-4 shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-white/20 rounded-full">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">AI Assistant</CardTitle>
                  <p className="text-xs text-white/80 font-normal">Online</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-white hover:bg-white/20 h-8 w-8"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 p-0 overflow-hidden bg-gray-50 relative">
            <div 
              ref={scrollRef}
              className="absolute inset-0 overflow-y-auto p-4 space-y-4 pb-20"
            >
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`
                      max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm
                      ${msg.role === 'user' 
                        ? 'bg-violet-600 text-white rounded-tr-sm' 
                        : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
                      }
                    `}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm flex gap-1">
                    <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce"></div>
                  </div>
                </div>
              )}
            </div>

            {/* Suggested Questions Chips */}
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-gray-50 via-gray-50/90 to-transparent">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none px-2">
                {suggestedQuestions.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(q)}
                    className="whitespace-nowrap flex-shrink-0 bg-white border border-violet-100 text-violet-700 text-xs px-3 py-1.5 rounded-full hover:bg-violet-50 transition-colors shadow-sm"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>

          <CardFooter className="p-3 bg-white border-t border-gray-100 shrink-0">
            <div className="flex w-full items-center gap-2">
              <Input 
                placeholder="Ask me anything..." 
                value={inputValue} 
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                className="flex-1 border-gray-200 focus-visible:ring-violet-500 bg-gray-50"
              />
              <Button 
                size="icon" 
                onClick={handleSend}
                disabled={!inputValue.trim() || isTyping}
                className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </CardFooter>
        </Card>
      )}

      <Button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300
          bg-gradient-to-r from-violet-600 to-indigo-600 hover:scale-105 active:scale-95
          flex items-center justify-center
        `}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Sparkles className="w-6 h-6 text-white" />
        )}
      </Button>
    </div>,
    document.body
  );
}
